/**
 * UV Disinfection Spec — standalone configurator and the source of `calcUV`
 * used by TrainEditPage. Models UV inactivation of E. coli / Giardia /
 * Cryptosporidium and total lamp count to deliver a target dose, accounting
 * for UVT (UV transmittance at 254 nm) and lamp efficiency.
 *
 * Standard target doses (mJ/cm²) for log-inactivation under USEPA UVDGM
 * Table 5.1 — Reynolds & Bauhm pre-RO polishing baseline of 40 mJ/cm² is
 * conservative for both Giardia and Cryptosporidium 4-log.
 */
import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Zap, Droplets, Gauge, CheckCircle2, AlertCircle, ShieldAlert,
  ArrowDown, Save, Beaker, FlaskConical, TrendingUp, BarChart3, Sun,
} from 'lucide-react';
import { useSampleLoader } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';

/* ─── Types ─────────────────────────────────────────────── */
export type UVLampType = 'low_pressure' | 'medium_pressure' | 'amalgam_low_pressure';

export interface UVParams {
  flowRate: number;          // m³/h
  uvtPct: number;            // % UV transmittance at 254 nm
  targetDoseMJ: number;      // mJ/cm² target dose (40 standard, 80 enhanced)
  lampType: UVLampType;
  numLamps: number;
  lampPowerW: number;        // W per lamp electrical
  uvEfficiency: number;      // 0–1 (LP UV ~ 0.35, MP UV ~ 0.18)
  chamberDiameter: number;   // m
  chamberLength: number;     // m
  pipeDiameter: number;      // mm (inlet/outlet pipe)
}

export interface UVResults {
  chamberVolume: number;     // m³
  ebct: number;              // s
  velocity: number;          // m/s in chamber
  uvIntensityMWcm2: number;  // mW/cm² estimated at the wall
  deliveredDose: number;     // mJ/cm² (USEPA RED bioassay-style)
  totalElectricalW: number;  // W
  uvOutputW: number;         // W of UV-C output
  energyKWhPerM3: number;    // kWh per m³ treated
  redundancyLevel: number;   // (1 = N, 2 = N+1, etc.) based on numLamps
  // Log-inactivation estimates (from delivered dose)
  logInactE_coli: number;
  logInactGiardia: number;
  logInactCrypto: number;
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overdesigned';
  statusDetail: string;
  warningLowUVT: boolean;
  warningHighFlow: boolean;
}

/* ─── Lamp dose constants for log-inactivation (USEPA UVDGM) ─── */
const DOSE_PER_LOG: Record<string, number> = {
  E_coli: 6.6,    // mJ/cm² per log
  Giardia: 11,    // mJ/cm² per log
  Crypto: 12,     // mJ/cm² per log
};

const LAMP_EFFICIENCY_DEFAULT: Record<UVLampType, number> = {
  low_pressure: 0.35,
  medium_pressure: 0.18,
  amalgam_low_pressure: 0.40,
};

const LAMP_POWER_TYPICAL: Record<UVLampType, number> = {
  low_pressure: 80,
  medium_pressure: 1000,
  amalgam_low_pressure: 320,
};

/* ─── Calc engine ─── */
export function calcUV(p: UVParams): UVResults {
  const area = Math.PI * Math.pow(p.chamberDiameter / 2, 2);
  const chamberVolume = area * p.chamberLength;
  const flowM3s = p.flowRate / 3600;
  const velocity = flowM3s / Math.max(0.0001, area);
  const ebct = chamberVolume / Math.max(0.0001, flowM3s);

  // UV-C output (germicidal)
  const totalElectricalW = p.numLamps * p.lampPowerW;
  const uvOutputW = totalElectricalW * p.uvEfficiency;

  // Average UV intensity at the wall (rough — accounting for chamber geometry)
  // I_avg = uvOutputW / (chamber wall area cm²) × UVT^pathLength
  const wallAreaM2 = Math.PI * p.chamberDiameter * p.chamberLength;
  const wallAreaCm2 = wallAreaM2 * 10000;
  const uvtFraction = Math.max(0.4, Math.min(0.99, p.uvtPct / 100));
  // Beer-Lambert path (~chamber radius in cm)
  const pathCm = (p.chamberDiameter / 2) * 100;
  const meanTransmission = Math.pow(uvtFraction, pathCm);
  const uvIntensityMWcm2 = (uvOutputW * 1000 / wallAreaCm2) * meanTransmission;

  // Delivered dose = I × t (mJ/cm² = mW/cm² × s)
  const deliveredDose = uvIntensityMWcm2 * ebct;

  // Log inactivation
  const logInactE_coli = deliveredDose / DOSE_PER_LOG.E_coli;
  const logInactGiardia = deliveredDose / DOSE_PER_LOG.Giardia;
  const logInactCrypto = deliveredDose / DOSE_PER_LOG.Crypto;

  // Energy use
  const energyKWhPerM3 = (totalElectricalW / 1000) / Math.max(0.0001, p.flowRate);

  // Redundancy
  const redundancyLevel = p.numLamps >= 4 ? 2 : p.numLamps >= 2 ? 1 : 0;

  // Status
  let status: UVResults['status']; let statusDetail: string;
  if (deliveredDose >= p.targetDoseMJ && uvIntensityMWcm2 >= 0.5 && velocity <= 1.0) {
    status = 'optimal';
    statusDetail = `Optimal. Delivered ${deliveredDose.toFixed(1)} mJ/cm² ≥ target ${p.targetDoseMJ}. UVT ${p.uvtPct}% acceptable.`;
  } else if (deliveredDose >= p.targetDoseMJ * 0.85) {
    status = 'acceptable';
    statusDetail = `Acceptable. ${deliveredDose.toFixed(1)} mJ/cm² is within 15% of target. Monitor lamp ageing.`;
  } else if (deliveredDose < p.targetDoseMJ * 0.5) {
    status = 'undersized';
    statusDetail = `Undersized. Only ${deliveredDose.toFixed(1)} mJ/cm² delivered vs ${p.targetDoseMJ} required. Add lamps, lower flow, or shorten chamber.`;
  } else {
    status = 'overdesigned';
    statusDetail = `Overdesigned. ${deliveredDose.toFixed(1)} mJ/cm² far exceeds need — consider fewer lamps.`;
  }

  return {
    chamberVolume, ebct, velocity, uvIntensityMWcm2, deliveredDose,
    totalElectricalW, uvOutputW, energyKWhPerM3, redundancyLevel,
    logInactE_coli, logInactGiardia, logInactCrypto,
    status, statusDetail,
    warningLowUVT: p.uvtPct < 75,
    warningHighFlow: velocity > 1.2,
  };
}

const DEFAULTS: UVParams = {
  flowRate: 24, uvtPct: 92, targetDoseMJ: 40,
  lampType: 'low_pressure', numLamps: 2, lampPowerW: 80, uvEfficiency: 0.35,
  chamberDiameter: 0.25, chamberLength: 1.0, pipeDiameter: 80,
};

function Slider({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange }: any) {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {step < 0.1 ? v.toFixed(2) : step < 1 ? v.toFixed(1) : v.toFixed(0)}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={v} onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: color, color, ['--p' as any]: `${Math.max(0, Math.min(100, ((v - min) / Math.max(0.0001, (max - min))) * 100)).toFixed(2)}%` }} />
    </div>
  );
}

function GaugeBar({ value, max, label, unit, color }: any) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const pct = Math.min(100, Math.max(0, (safeValue / Math.max(0.0001, max)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold" style={{ color }}>{safeValue.toFixed(1)} {unit}</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

const LS_KEY = 'rb_uv_spec';

export default function UVDisinfectionSpecPage() {
  const sampleLoader = useSampleLoader();
  const [params, setParams] = useState<UVParams>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) return { ...DEFAULTS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  // Update lamp defaults when type changes
  useEffect(() => {
    setParams(p => ({
      ...p,
      uvEfficiency: LAMP_EFFICIENCY_DEFAULT[p.lampType],
      lampPowerW: LAMP_POWER_TYPICAL[p.lampType],
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.lampType]);

  useEffect(() => {
    const sp: any = sampleLoader.baseSampleParams;
    if (!sp) return;
    setParams(p => ({ ...p, flowRate: sp.flowRate ? sp.flowRate / 24 : p.flowRate }));
  }, [sampleLoader.baseSampleParams]);

  const results = useMemo(() => calcUV(params), [params]);
  const update = <K extends keyof UVParams>(k: K, v: UVParams[K]) => setParams(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(params)); setSaved(true); toast.success('UV spec saved'); setTimeout(() => setSaved(false), 1500); }
    catch { toast.error('Save failed'); }
  };

  const statusColors: any = {
    optimal: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
    acceptable: { bg: 'bg-sky-500/5', border: 'border-sky-500/20', text: 'text-sky-400', icon: CheckCircle2 },
    undersized: { bg: 'bg-rose-500/5', border: 'border-rose-500/20', text: 'text-rose-400', icon: ShieldAlert },
    overdesigned: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', text: 'text-amber-400', icon: AlertCircle },
  };
  const sc = statusColors[results.status];
  const StatusIcon = sc.icon;

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="UV Disinfection"
        subtitle="UV-C inactivation of bacteria, viruses & protozoa. Polishing step downstream of UF / RO."
        icon={Zap}
        actions={
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20">
            <Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}
          </button>
        }
      />

      <SampleSelector loader={sampleLoader} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="p-4 rb-card-sm">
            <p className="text-[11px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
              <ArrowDown className="w-3.5 h-3.5" /> UV Reactor — Cross Section
            </p>
            <svg viewBox="0 0 900 360" className="w-full h-auto">
              <defs>
                <linearGradient id="uvWater" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#0f2847" stopOpacity="0.9" />
                </linearGradient>
                <radialGradient id="uvLamp" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#a855f7" stopOpacity="1" />
                  <stop offset="60%" stopColor="#7e22ce" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                </radialGradient>
              </defs>

              <text x="450" y="28" fill="#cbd5e1" fontSize="16" textAnchor="middle" fontWeight="bold" letterSpacing="2">UV DISINFECTION REACTOR</text>

              {/* Inlet */}
              <rect x="40" y="120" width="120" height="100" rx="8" fill="url(#uvWater)" stroke="#38bdf8" strokeWidth="2.5" />
              <text x="100" y="142" fill="#38bdf8" fontSize="14" textAnchor="middle" fontWeight="bold">Inlet</text>
              <text x="100" y="166" fill="#e2e8f0" fontSize="13" textAnchor="middle">{params.flowRate.toFixed(1)} m³/h</text>
              <text x="100" y="186" fill="#94a3b8" fontSize="12" textAnchor="middle">UVT {params.uvtPct}%</text>
              <text x="100" y="206" fill="#94a3b8" fontSize="12" textAnchor="middle">Ø{params.pipeDiameter} mm</text>

              {/* Pipe to chamber */}
              <line x1="160" y1="170" x2="270" y2="170" stroke="#38bdf8" strokeWidth="3" />
              <polygon points="268,164 278,170 268,176" fill="#38bdf8" />

              {/* Chamber */}
              <rect x="280" y="110" width="340" height="120" rx="12" fill="url(#uvWater)" stroke="#a855f7" strokeWidth="3" />
              <text x="450" y="100" fill="#a855f7" fontSize="14" textAnchor="middle" fontWeight="bold">UV CHAMBER · {params.numLamps} × {params.lampType.replace('_', ' ').toUpperCase()}</text>

              {/* Lamps */}
              {Array.from({ length: params.numLamps }).map((_, i) => {
                const lampY = 110 + 25 + (i + 1) * (90 / (params.numLamps + 1));
                return (
                  <g key={i}>
                    <ellipse cx="450" cy={lampY} rx="160" ry="6" fill="url(#uvLamp)" opacity="0.9" />
                    <line x1="295" y1={lampY} x2="605" y2={lampY} stroke="#c084fc" strokeWidth="3" strokeLinecap="round" />
                    <circle cx="295" cy={lampY} r="6" fill="#a855f7" />
                    <circle cx="605" cy={lampY} r="6" fill="#a855f7" />
                  </g>
                );
              })}

              <text x="450" y="248" fill="#94a3b8" fontSize="12" textAnchor="middle">
                Ø{params.chamberDiameter.toFixed(2)}m · L {params.chamberLength.toFixed(2)}m · EBCT {results.ebct.toFixed(1)}s · v {results.velocity.toFixed(2)} m/s
              </text>

              {/* Outlet */}
              <line x1="620" y1="170" x2="730" y2="170" stroke="#10b981" strokeWidth="3" />
              <polygon points="728,164 738,170 728,176" fill="#10b981" />
              <rect x="740" y="120" width="120" height="100" rx="8" fill="url(#uvWater)" stroke="#10b981" strokeWidth="2.5" />
              <text x="800" y="142" fill="#10b981" fontSize="14" textAnchor="middle" fontWeight="bold">Outlet</text>
              <text x="800" y="166" fill="#e2e8f0" fontSize="13" textAnchor="middle">Dose {results.deliveredDose.toFixed(1)}</text>
              <text x="800" y="186" fill="#94a3b8" fontSize="12" textAnchor="middle">mJ/cm²</text>
              <text x="800" y="206" fill={results.deliveredDose >= params.targetDoseMJ ? '#10b981' : '#f43f5e'} fontSize="13" textAnchor="middle" fontWeight="bold">
                {results.deliveredDose >= params.targetDoseMJ ? '✓ target met' : '✗ below target'}
              </text>

              {/* KPI panel */}
              <g transform="translate(40, 260)">
                <rect x="0" y="0" width="820" height="80" rx="8" fill="#0f172a" stroke="#1e293b" />
                <text x="10" y="22" fill="#94a3b8" fontSize="12" fontWeight="bold">PERFORMANCE</text>
                <text x="10" y="46" fill="#64748b" fontSize="12">UV intensity</text>
                <text x="10" y="62" fill="#a855f7" fontSize="14" fontWeight="bold">{results.uvIntensityMWcm2.toFixed(2)} mW/cm²</text>
                <text x="170" y="46" fill="#64748b" fontSize="12">E. coli log inact.</text>
                <text x="170" y="62" fill="#10b981" fontSize="14" fontWeight="bold">{results.logInactE_coli.toFixed(1)}</text>
                <text x="320" y="46" fill="#64748b" fontSize="12">Giardia log inact.</text>
                <text x="320" y="62" fill="#10b981" fontSize="14" fontWeight="bold">{results.logInactGiardia.toFixed(1)}</text>
                <text x="470" y="46" fill="#64748b" fontSize="12">Crypto log inact.</text>
                <text x="470" y="62" fill="#10b981" fontSize="14" fontWeight="bold">{results.logInactCrypto.toFixed(1)}</text>
                <text x="620" y="46" fill="#64748b" fontSize="12">Energy</text>
                <text x="620" y="62" fill="#fbbf24" fontSize="14" fontWeight="bold">{results.energyKWhPerM3.toFixed(3)} kWh/m³</text>
              </g>
            </svg>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-5">
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>

          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{results.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{results.statusDetail}</p>
          </div>

          {results.warningLowUVT && (
            <div className="rounded-xl border p-3 bg-amber-500/5 border-amber-500/20">
              <p className="text-[11px] text-amber-400 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" />UVT {params.uvtPct}% is low — lamp ageing & fouling will quickly drop the delivered dose. Strengthen upstream filtration or increase lamps/dose.
              </p>
            </div>
          )}

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Lamp & Reactor</h3>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Lamp type</label>
              <select value={params.lampType} onChange={e => update('lampType', e.target.value as UVLampType)}
                className="w-full mt-1 bg-rb-elevated border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
                <option value="low_pressure">Low-pressure (LP) — narrow-band 254 nm, ~35% efficient</option>
                <option value="amalgam_low_pressure">Amalgam LP — high-output 254 nm, ~40% efficient</option>
                <option value="medium_pressure">Medium-pressure (MP) — polychromatic, ~18% efficient</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Lamps" value={params.numLamps} min={1} max={12} step={1} unit="" icon={Sun} color="#a855f7" onChange={(v: number) => update('numLamps', v)} />
              <Slider label="Lamp power" value={params.lampPowerW} min={40} max={2000} step={10} unit="W" icon={Zap} color="#a855f7" onChange={(v: number) => update('lampPowerW', v)} />
              <Slider label="UV efficiency" value={params.uvEfficiency * 100} min={10} max={50} step={1} unit="%" icon={Zap} color="#a855f7" onChange={(v: number) => update('uvEfficiency', v / 100)} />
              <Slider label="Chamber Ø" value={params.chamberDiameter} min={0.1} max={1.2} step={0.05} unit="m" icon={Gauge} color="#a855f7" onChange={(v: number) => update('chamberDiameter', v)} />
              <Slider label="Chamber length" value={params.chamberLength} min={0.4} max={3} step={0.1} unit="m" icon={Gauge} color="#a855f7" onChange={(v: number) => update('chamberLength', v)} />
            </div>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Process</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Slider label="Flow" value={params.flowRate} min={1} max={300} step={1} unit="m³/h" icon={Droplets} color="#38bdf8" onChange={(v: number) => update('flowRate', v)} />
              <Slider label="UVT @ 254 nm" value={params.uvtPct} min={50} max={99} step={1} unit="%" icon={Sun} color="#fbbf24" onChange={(v: number) => update('uvtPct', v)} />
              <Slider label="Target dose" value={params.targetDoseMJ} min={20} max={200} step={5} unit="mJ/cm²" icon={TrendingUp} color="#10b981" onChange={(v: number) => update('targetDoseMJ', v)} />
              <Slider label="Pipe Ø" value={params.pipeDiameter} min={25} max={300} step={5} unit="mm" icon={Gauge} color="#94a3b8" onChange={(v: number) => update('pipeDiameter', v)} />
            </div>
          </div>

          <div className="p-4 rb-inner-card space-y-3">
            <h3 className="text-xs font-semibold text-slate-200">Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <GaugeBar value={results.deliveredDose} max={params.targetDoseMJ * 2} label="Delivered dose" unit="mJ/cm²" color="#a855f7" />
              <GaugeBar value={results.uvIntensityMWcm2} max={2} label="UV intensity" unit="mW/cm²" color="#a855f7" />
              <GaugeBar value={results.logInactGiardia} max={6} label="Giardia log" unit="" color="#10b981" />
              <GaugeBar value={results.logInactCrypto} max={6} label="Crypto log" unit="" color="#10b981" />
            </div>
          </div>

          <div className="p-4 rb-card space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <div className="space-y-3">
              {[
                { title: 'Why UV needs clean upstream water',
                  content: `UV inactivates microbes but cannot kill what it cannot reach. Particles >2 µm shadow microbes inside them — the USEPA UVDGM requires SDI <3 and ideally turbidity <0.3 NTU. Place UV downstream of UF or cartridge polish, never before particulate removal.` },
                { title: 'UVT vs delivered dose',
                  content: `UVT (% UV transmittance at 254 nm) governs how far UV-C light penetrates the water column. Borehole / RO permeate typically 95–98% UVT; surface water 75–90%; high-iron water can drop below 70%. Beer-Lambert path through ${(params.chamberDiameter/2*100).toFixed(0)} cm at ${params.uvtPct}% UVT → mean transmission ${(Math.pow(params.uvtPct/100, params.chamberDiameter/2*100)*100).toFixed(1)}%.` },
                { title: 'USEPA UVDGM dose targets',
                  content: `4-log inactivation: E. coli ~25 mJ/cm², Giardia ~22 mJ/cm², Cryptosporidium ~22 mJ/cm². Standard pre-RO polishing: 40 mJ/cm² provides 6+ log E. coli and 3.6 log Giardia. With fouling/ageing factors (typ. 0.7), required delivered dose ≈ target × 1.4.` },
                { title: 'Lamp economics',
                  content: `LP lamps: highest UV-C efficiency, low W/lamp, long life (12,000 h), simple. MP lamps: 10× higher power per lamp, polychromatic (also inactivates UV-resistant Adenovirus), shorter life (5,000 h), needs cooling. Amalgam LP: 4× LP output per lamp, sweet spot for mid-range duties. Energy: ${results.energyKWhPerM3.toFixed(3)} kWh/m³ at current config.` },
              ].map((s, i) => (
                <details key={i} className="rounded-lg border border-slate-800 bg-rb-elevated/40">
                  <summary className="cursor-pointer p-3 text-xs font-bold text-sky-400 hover:text-sky-300">{s.title}</summary>
                  <p className="px-3 pb-3 text-[11px] text-slate-300 leading-relaxed">{s.content}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
